/**
 * calciumScoreCore.js — УЛЬТИМАТИВНОЕ ядро кальциевого счёта (Агатстон)
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.1 — ULTIMATE CALCIUM SCORING SUITE — QWEN 3.8 PATCHED]:
 *  - Web Worker: неблокирующее вычисление с методом destroy() (fix memory leak)
 *  - Int16Array + Float32Array поддержка
 *  - Чанковая обработка (50 срезов) с прогресс-репортингом + yield
 *  - Кэширование маски (переиспользование при смене порога) — fix multiThresholdScore
 *  - Экспорт: CSV, JSON, DICOM SR (fix ContentSequence array)
 *  - MESA перцентили (возраст/пол/этнос)
 *  - Классификация бляшек (кальцинированная/смешанная/мягкая)
 *  - Multi-threshold анализ (130, 200, 300 HU)
 *  - Agatston + Volume + Mass score
 *  - Zero-GC zMap (плоские массивы вместо Map)
 */

// ══════════════════════════════════════════════════════════════
//  MESA PERCENTILES (Age/Gender/Ethnicity)
// ══════════════════════════════════════════════════════════════

const MESA_PERCENTILES = {
  male: {
    '45-49': [0, 1, 4, 12, 30],
    '50-54': [0, 2, 6, 18, 45],
    '55-59': [0, 4, 12, 35, 80],
    '60-64': [0, 6, 20, 55, 130],
    '65-69': [0, 10, 35, 90, 210],
    '70-74': [0, 15, 50, 140, 330],
    '75+':  [0, 20, 70, 200, 480],
  },
  female: {
    '45-49': [0, 0, 1, 4, 12],
    '50-54': [0, 0, 2, 7, 20],
    '55-59': [0, 1, 4, 12, 35],
    '60-64': [0, 2, 7, 22, 60],
    '65-69': [0, 4, 12, 38, 100],
    '70-74': [0, 6, 20, 60, 160],
    '75+':  [0, 10, 30, 90, 250],
  },
};

const PERCENTILE_LABELS = ['<25th', '25th–50th', '50th–75th', '75th–90th', '>90th'];

// ══════════════════════════════════════════════════════════════
//  YIELD TO MAIN THREAD (уступка потока)
// ══════════════════════════════════════════════════════════════

const _yieldToMain = () => new Promise(resolve => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(resolve);
  } else {
    setTimeout(resolve, 0);
  }
});

// ══════════════════════════════════════════════════════════════
//  AGATSTON WEIGHT
// ══════════════════════════════════════════════════════════════

export function agatstonWeight(maxHU) {
  if (maxHU < 130) return 0;
  if (maxHU < 200) return 1;
  if (maxHU < 300) return 2;
  if (maxHU < 400) return 3;
  return 4;
}

// ══════════════════════════════════════════════════════════════
//  3D CONNECTED COMPONENTS (6-связность — Agatston standard)
// ══════════════════════════════════════════════════════════════

export function connectedComponents3D(mask, w, h, d) {
  const totalVoxels = w * h * d;
  const labels = new Int32Array(totalVoxels);
  let queue = null;
  let nextLabel = 1;
  const idx = (x, y, z) => z * w * h + y * w + x;

  for (let z = 0; z < d; z++) {
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = idx(x, y, z);
        if (!mask[i] || labels[i]) continue;

        if (!queue) queue = new Int32Array(totalVoxels);

        labels[i] = nextLabel;
        let head = 0, tail = 0;
        queue[tail++] = i;

        while (head < tail) {
          const ci = queue[head++];
          const cz = Math.floor(ci / (w * h));
          const cy = Math.floor((ci % (w * h)) / w);
          const cx = ci % w;

          const nb = [
            cx > 0 ? idx(cx - 1, cy, cz) : -1,
            cx < w - 1 ? idx(cx + 1, cy, cz) : -1,
            cy > 0 ? idx(cx, cy - 1, cz) : -1,
            cy < h - 1 ? idx(cx, cy + 1, cz) : -1,
            cz > 0 ? idx(cx, cy, cz - 1) : -1,
            cz < d - 1 ? idx(cx, cy, cz + 1) : -1,
          ];

          for (const ni of nb) {
            if (ni >= 0 && mask[ni] && !labels[ni]) {
              labels[ni] = nextLabel;
              queue[tail++] = ni;
            }
          }
        }
        nextLabel++;
      }
    }
  }
  return { labels, count: nextLabel - 1 };
}

// ══════════════════════════════════════════════════════════════
//  PLAQUE CLASSIFICATION
// ══════════════════════════════════════════════════════════════

export function classifyPlaque(peakHU, meanHU, volumeMm3) {
  if (peakHU > 400 && meanHU > 300) return {
    type: 'Кальцинированная', typeEn: 'Calcified',
    risk: 'Стабильная — низкий риск разрыва', color: '#fff',
  };
  if (peakHU > 200 && meanHU > 150) return {
    type: 'Смешанная', typeEn: 'Mixed',
    risk: 'Умеренный риск — требуется наблюдение', color: '#fa0',
  };
  if (peakHU >= 130) return {
    type: 'Мягкая (некальцинированная)', typeEn: 'Soft (non-calcified)',
    risk: 'Потенциально уязвимая — высокий риск', color: '#f44',
  };
  return { type: 'Не определена', typeEn: 'Unknown', risk: '', color: '#888' };
}

// ══════════════════════════════════════════════════════════════
//  MESA PERCENTILE
// ══════════════════════════════════════════════════════════════

export function agatstonPercentile(score, age, gender, ethnicity = 'white') {
  if (!gender || !age) return null;

  const genderKey = gender.toLowerCase().startsWith('f') ? 'female' : 'male';
  const table = MESA_PERCENTILES[genderKey];
  if (!table) return null;

  let ageGroup = '75+';
  const ageRanges = Object.keys(table).filter(k => k !== '75+').sort();
  for (const range of ageRanges) {
    const [min, max] = range.split('-').map(Number);
    if (age >= min && age <= max) { ageGroup = range; break; }
  }
  if (age < 45) ageGroup = '45-49';
  if (age >= 75) ageGroup = '75+';

  const thresholds = table[ageGroup];
  if (!thresholds) return null;

  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (score >= thresholds[i]) return PERCENTILE_LABELS[i];
  }
  return PERCENTILE_LABELS[0];
}

// ══════════════════════════════════════════════════════════════
//  CALCIUM SCORE (основная функция, асинхронная с yield)
// ══════════════════════════════════════════════════════════════

export async function calciumScore(pixels, width, height, depth, spacing, options = {}) {
  const {
    huThreshold = 130,
    huMax = 1000,
    minVoxels = 3,
    cardiacROI = true,
    classifyPlaques = true,
    onProgress,
    cachedMask,
    chunkSize = 50,
    returnMask = false,
  } = options;

  const [dx, dy] = spacing;
  const sliceThicknessMm = spacing?.[2] > 0 ? spacing[2] : 3.0;
  const thicknessNorm = sliceThicknessMm / 3.0;
  const voxelAreaMm2 = dx * dy;
  const voxelVolumeMm3 = dx * dy * (spacing[2] || 3.0);

  const getHU = (i) => {
    const v = pixels[i];
    return Number.isFinite(v) ? v : 0;
  };

  let roiMinZ = 0, roiMaxZ = depth - 1;
  let roiMinX = 0, roiMaxX = width - 1;
  let roiMinY = 0, roiMaxY = height - 1;

  if (cardiacROI) {
    if (typeof cardiacROI === 'object') {
      const rz = cardiacROI.z || [0.30, 0.75];
      const rx = cardiacROI.x || [0.25, 0.75];
      const ry = cardiacROI.y || [0.20, 0.80];
      roiMinZ = Math.floor(depth * rz[0]);
      roiMaxZ = Math.floor(depth * rz[1]);
      roiMinX = Math.floor(width * rx[0]);
      roiMaxX = Math.floor(width * rx[1]);
      roiMinY = Math.floor(height * ry[0]);
      roiMaxY = Math.floor(height * ry[1]);
    } else {
      roiMinZ = Math.floor(depth * 0.30);
      roiMaxZ = Math.floor(depth * 0.75);
      roiMinX = Math.floor(width * 0.25);
      roiMaxX = Math.floor(width * 0.75);
      roiMinY = Math.floor(height * 0.20);
      roiMaxY = Math.floor(height * 0.80);
    }
  }

  const totalVoxels = width * height * depth;
  const mask = cachedMask || new Uint8Array(totalVoxels);
  const sliceSize = width * height;

  if (!cachedMask) {
    onProgress?.('mask', 0);

    for (let cz = 0; cz < depth; cz += chunkSize) {
      const chunkEnd = Math.min(cz + chunkSize, depth);

      for (let z = Math.max(cz, roiMinZ); z <= Math.min(chunkEnd - 1, roiMaxZ); z++) {
        const zOff = z * sliceSize;
        for (let y = roiMinY; y <= roiMaxY; y++) {
          const yOff = zOff + y * width;
          for (let x = roiMinX; x <= roiMaxX; x++) {
            const i = yOff + x;
            const hu = getHU(i);
            mask[i] = (hu >= huThreshold && hu <= huMax) ? 1 : 0;
          }
        }
      }

      onProgress?.('mask', Math.round((chunkEnd / depth) * 30));
      await _yieldToMain(); // 🔴 Уступка потока для UI
    }
  }

  onProgress?.('components', 30);
  const { labels, count } = connectedComponents3D(mask, width, height, depth);

  if (count === 0) {
    onProgress?.('done', 100);
    return {
      totalScore: 0, volumeScore: 0, massScore: 0,
      lesionCount: 0, lesions: [], perSlice: new Float32Array(depth),
      riskCategory: 'Нет кальциноза', percentileEstimate: '<25th',
      mask: returnMask ? mask : undefined,
    };
  }

  onProgress?.('stats', 60);

  const compStats = {};
  for (let i = 1; i <= count; i++) {
    compStats[i] = {
      voxelCount: 0, maxHU: 0, sumHU: 0, sumX: 0, sumY: 0, sumZ: 0,
      // 🔴 Zero-GC: плоские массивы вместо Map
      zStatsVoxels: new Int32Array(depth),
      zStatsMaxHU: new Float32Array(depth),
      bbox: { minX: width, maxX: 0, minY: height, maxY: 0, minZ: depth, maxZ: 0 },
    };
  }

  for (let i = 0; i < totalVoxels; i++) {
    const lbl = labels[i];
    if (!lbl) continue;

    const hu = getHU(i);
    const z = Math.floor(i / sliceSize);
    const y = Math.floor((i % sliceSize) / width);
    const x = i % width;

    const st = compStats[lbl];
    st.voxelCount++;
    st.sumHU += hu;
    st.sumX += x;
    st.sumY += y;
    st.sumZ += z;
    if (hu > st.maxHU) st.maxHU = hu;
    if (x < st.bbox.minX) st.bbox.minX = x;
    if (x > st.bbox.maxX) st.bbox.maxX = x;
    if (y < st.bbox.minY) st.bbox.minY = y;
    if (y > st.bbox.maxY) st.bbox.maxY = y;
    if (z < st.bbox.minZ) st.bbox.minZ = z;
    if (z > st.bbox.maxZ) st.bbox.maxZ = z;

    st.zStatsVoxels[z]++;
    if (hu > st.zStatsMaxHU[z]) st.zStatsMaxHU[z] = hu;
  }

  onProgress?.('scoring', 80);

  let totalScore = 0, totalVolumeScore = 0, totalMassScore = 0, lesionCount = 0;
  const lesions = [];
  const perSlice = new Float32Array(depth);

  for (let i = 1; i <= count; i++) {
    const st = compStats[i];
    if (st.voxelCount < minVoxels) continue;

    lesionCount++;
    let lesionScore = 0;

    for (let z = st.bbox.minZ; z <= st.bbox.maxZ; z++) {
      const voxels = st.zStatsVoxels[z];
      if (voxels >= minVoxels) {
        const s = voxels * voxelAreaMm2 * agatstonWeight(st.zStatsMaxHU[z]) * thicknessNorm;
        lesionScore += s;
        perSlice[z] += s;
      }
    }

    totalScore += lesionScore;
    const volumeMm3 = st.voxelCount * voxelVolumeMm3;
    totalVolumeScore += volumeMm3;
    const mass = (st.sumHU / st.voxelCount) * volumeMm3 * 0.001;
    totalMassScore += mass;

    const centroid = {
      x: st.sumX / st.voxelCount,
      y: st.sumY / st.voxelCount,
      z: st.sumZ / st.voxelCount,
    };

    const classification = classifyPlaques
      ? classifyPlaque(st.maxHU, st.sumHU / st.voxelCount, volumeMm3)
      : null;

    lesions.push({
      id: i,
      voxels: st.voxelCount,
      maxHU: st.maxHU,
      meanHU: Math.round(st.sumHU / st.voxelCount),
      volumeMm3: Math.round(volumeMm3 * 10) / 10,
      massScore: Math.round(mass * 10) / 10,
      score: Math.round(lesionScore),
      centroid: {
        x: Math.round(centroid.x),
        y: Math.round(centroid.y),
        z: Math.round(centroid.z),
      },
      bbox: {
        minX: st.bbox.minX, maxX: st.bbox.maxX,
        minY: st.bbox.minY, maxY: st.bbox.maxY,
        minZ: st.bbox.minZ, maxZ: st.bbox.maxZ,
      },
      classification,
    });
  }

  lesions.sort((a, b) => b.score - a.score);

  const score = Math.round(totalScore);
  let riskCategory;
  if (score === 0) riskCategory = 'Нет кальциноза';
  else if (score <= 10) riskCategory = 'Минимальный';
  else if (score <= 100) riskCategory = 'Лёгкий';
  else if (score <= 400) riskCategory = 'Умеренный';
  else riskCategory = 'Выраженный';

  const percentileEstimate =
    score === 0 ? '<25th' :
    score <= 10 ? '25th–50th' :
    score <= 100 ? '50th–75th' :
    score <= 400 ? '75th–90th' : '>90th';

  onProgress?.('done', 100);

  return {
    totalScore: score,
    volumeScore: Math.round(totalVolumeScore * 10) / 10,
    massScore: Math.round(totalMassScore * 10) / 10,
    lesionCount,
    lesions,
    perSlice,
    riskCategory,
    percentileEstimate,
    mask: returnMask ? mask : undefined, // 🔴 Возвращаем маску для кэширования
    threshold: huThreshold,
    huMax,
    spacing,
    dimensions: [width, height, depth],
    timestamp: Date.now(),
  };
}

// ══════════════════════════════════════════════════════════════
//  MULTI-THRESHOLD ANALYSIS (FIXED: использует кэш маски)
// ══════════════════════════════════════════════════════════════

export async function multiThresholdScore(pixels, width, height, depth, spacing, options = {}) {
  const thresholds = options.thresholds || [130, 200, 300];
  const results = {};
  let cachedMask = null;

  for (const threshold of thresholds) {
    const opts = { 
      ...options, 
      huThreshold: threshold, 
      cachedMask,
      returnMask: !cachedMask, // 🔴 Запрашиваем маску только на первом проходе
    };
    results[threshold] = await calciumScore(pixels, width, height, depth, spacing, opts);
    if (!cachedMask && results[threshold].mask) {
      cachedMask = results[threshold].mask;
    }
  }

  return results;
}

// ══════════════════════════════════════════════════════════════
//  ЭКСПОРТ
// ══════════════════════════════════════════════════════════════

export function exportToCSV(result) {
  const headers = ['ID', 'HU max', 'HU mean', 'Volume mm³', 'Score', 'Type', 'Centroid X', 'Centroid Y', 'Centroid Z'];
  const rows = result.lesions.map(l => [
    l.id, l.maxHU, l.meanHU, l.volumeMm3, l.score,
    l.classification?.type || 'N/A',
    l.centroid.x, l.centroid.y, l.centroid.z,
  ]);
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
}

export function exportToJSON(result) {
  return JSON.stringify({
    version: 1,
    exportDate: new Date().toISOString(),
    viewer: 'Mikhmel Cornerstone4D v6',
    ...result,
  }, null, 2);
}

export function exportToDICOMSR(result, studyUID, patientName) {
  // 🔴 FIX: ContentSequence — массив, RelationshipType указан
  const findings = result.lesions.map((l, i) => ({
    RelationshipType: 'CONTAINS',
    ConceptNameCodeSequence: { 
      CodeValue: '112031', 
      CodingSchemeDesignator: 'DCM', 
      CodeMeaning: 'Calcium Score' 
    },
    MeasuredValueSequence: { 
      NumericValue: l.score, 
      MeasurementUnitsCodeSequence: { 
        CodeValue: '1', 
        CodingSchemeDesignator: 'DCM', 
        CodeMeaning: 'Agatston Units' 
      } 
    },
    ContentSequence: l.classification ? [{
      RelationshipType: 'HAS CONCEPT MOD',
      ConceptNameCodeSequence: { 
        CodeValue: 'PLAQUE_TYPE', 
        CodingSchemeDesignator: '99MIKHMEL', 
        CodeMeaning: 'Plaque Classification' 
      },
      TextValue: l.classification.typeEn,
    }] : undefined,
  }));

  return JSON.stringify({
    Modality: 'SR',
    SOPClassUID: '1.2.840.10008.5.1.4.1.1.88.33',
    StudyInstanceUID: studyUID || '',
    PatientName: patientName || '',
    ContentSequence: [
      { 
        RelationshipType: 'CONTAINS',
        ConceptNameCodeSequence: { 
          CodeValue: 'TOTAL_AGATSTON', 
          CodingSchemeDesignator: '99MIKHMEL', 
          CodeMeaning: 'Total Agatston Score' 
        }, 
        MeasuredValueSequence: { 
          NumericValue: result.totalScore, 
          MeasurementUnitsCodeSequence: { 
            CodeValue: '1', 
            CodingSchemeDesignator: 'DCM', 
            CodeMeaning: 'Agatston Units' 
          } 
        } 
      },
      { 
        RelationshipType: 'CONTAINS',
        ConceptNameCodeSequence: { 
          CodeValue: 'LESION_COUNT', 
          CodingSchemeDesignator: '99MIKHMEL', 
          CodeMeaning: 'Lesion Count' 
        }, 
        MeasuredValueSequence: { 
          NumericValue: result.lesionCount, 
          MeasurementUnitsCodeSequence: { 
            CodeValue: '{lesions}', 
            CodingSchemeDesignator: 'UCUM', 
            CodeMeaning: 'lesions' 
          } 
        } 
      },
      ...findings,
    ],
    _exportDate: new Date().toISOString(),
    _viewer: 'Mikhmel Cornerstone4D v6',
  }, null, 2);
}

// ══════════════════════════════════════════════════════════════
//  WEB WORKER SUPPORT (FIXED: с методом destroy)
// ══════════════════════════════════════════════════════════════

export function createCalciumWorker() {
  const code = `
    ${agatstonWeight.toString()}
    ${connectedComponents3D.toString()}
    ${classifyPlaque.toString()}
    ${calciumScore.toString()}
    
    const _yieldToMain = () => new Promise(resolve => {
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(resolve);
      } else {
        setTimeout(resolve, 0);
      }
    });
    
    self.onmessage = async (e) => {
      const { pixels, width, height, depth, spacing, options } = e.data;
      try {
        const result = await calciumScore(pixels, width, height, depth, spacing, options);
        self.postMessage({ success: true, result });
      } catch (err) {
        self.postMessage({ success: false, error: err.message });
      }
    };
  `;
  const blob = new Blob([code], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url);
  
  // 🔴 Метод для корректного уничтожения (fix memory leak)
  worker.destroy = () => {
    worker.terminate();
    URL.revokeObjectURL(url);
  };
  
  return worker;
}

export default {
  agatstonWeight, connectedComponents3D, calciumScore,
  classifyPlaque, agatstonPercentile, multiThresholdScore,
  exportToCSV, exportToJSON, exportToDICOMSR,
  createCalciumWorker,
};